import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Box, Chip, IconButton, InputAdornment, Paper, Stack,
    TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import dayjs from "dayjs";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DirectionsCarRoundedIcon from "@mui/icons-material/DirectionsCarRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import LocalGasStationRoundedIcon from "@mui/icons-material/LocalGasStationRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import PhotoCameraRoundedIcon from "@mui/icons-material/PhotoCameraRounded";
import ImageNotSupportedRoundedIcon from "@mui/icons-material/ImageNotSupportedRounded";

import ResizableTable from "../../component/common/ResizableTable";
import FormModal from "../../component/common/FormModal";
import FormField from "../../component/common/FormField";
import ComboBox from "../../component/common/ComboBox";
import ActionButton from "../../component/common/ActionButton";
import LoadingDots from "../../component/common/LoadingDots";
import { vehicleApi } from "./api/vehicleApi";
import { useMyPermissions } from "../../util/useMyPermissions";
import { runApiAction } from "../../util/apiFeedback";
import { confirm as confirmDialog } from "../../util/confirmDialog";
import { formatDate } from "../../util/dateUtils";
import { formatNumberWithCommas, formatKrw, toNumericInputState } from "../../util/numberFormat";
import { safeImgUrl } from "../../util/imageUrl";
import { ds } from "../../styles/designSystem";

const C = ds.colors;

/** 차량 카드 이미지 비율 — 첫 화면에 5대가 한눈에 들어오는 크기. */
const CARD_IMAGE_RATIO = "62%";

const todayStr = () => formatDate(new Date());

const emptyLog = (vehicleId) => ({
    id: null,
    vehicleId: vehicleId ?? "",
    driveDate: todayStr(),
    purpose: "BUSINESS",
    purposeDetail: "",
    origin: "",
    destination: "",
    odometerBefore: "",
    odometerAfter: "",
    refuelAmount: "",
    note: "",
});

const emptyVehicle = () => ({
    id: null, name: "", plateNo: "", model: "",
    initialOdometer: "", memo: "", active: true, logCount: 0,
});

/**
 * 등록 시 계기판을 잠글지 — 운행 기록이 한 건이라도 쌓인 뒤부터.
 * 이 값은 첫 기록의 '주행 전' 기준점이라 기록이 있는데 바꾸면 누적 KM 이 꼬인다.
 * 기록이 없으면 꼬일 대상이 없으니 언제든 고칠 수 있다.
 */
const isOdometerLocked = (f) => Boolean(f?.id) && (f?.logCount || 0) > 0;

/** 등록 시 계기판 입력칸 안내 — 지금 왜 열려 있는지/왜 잠겼는지를 그 자리에서 알려준다. */
const odometerHelper = (f) => {
    if (isOdometerLocked(f)) return `운행 기록 ${f.logCount}건이 있어 변경할 수 없습니다 (누적 KM 기준점)`;
    if (f?.id) return "운행 기록이 없어 아직 수정할 수 있습니다";
    return "운행 기록이 쌓이기 전까지는 수정할 수 있습니다";
};

/** 숫자 입력 → 정수 (빈칸/비숫자는 null). */
const toInt = (v) => {
    if (v === "" || v === null || v === undefined) return null;
    const n = Number(String(v).replace(/[^0-9-]/g, ""));
    return Number.isFinite(n) ? n : null;
};

const kmText = (v) => (v == null ? "—" : `${formatNumberWithCommas(v)}`);

/** 숫자 입력칸 표시값 — 타이핑하는 동안 세 자리마다 콤마를 찍는다. 저장 시엔 toInt 가 콤마를 걷어낸다. */
const commaInput = (raw) => toNumericInputState(raw).display;

/* ─── 폼 레이아웃 공통 ────────────────────────────────────────────
   폼의 모든 줄은 같은 2열 그리드를 쓴다. 칸마다 flex 비율을 따로 주면
   줄마다 좌우 칸의 시작·끝선이 어긋나 길이가 뒤죽박죽으로 보인다. */

/** 한 줄 = 같은 폭의 두 칸. 좁은 화면에선 한 칸씩 쌓인다. */
const FORM_ROW_SX = {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
    gap: 1.5,
    alignItems: "start",
};
/** 그리드 칸 안에서 필드가 칸을 꽉 채우게 한다 (긴 값이 칸을 밀어내지 않도록 minWidth:0). */
const FIELD_SX = { minWidth: 0, width: "100%" };

function FormRow({ children }) {
    return <Box sx={FORM_ROW_SX}>{children}</Box>;
}

/** 폼 구역 제목 — 항목이 많아 한 덩어리로 보면 어디까지가 한 묶음인지 안 보인다. */
function SectionLabel({ children }) {
    return (
        <Typography sx={{
            fontSize: 11, fontWeight: 800, color: C.textMuted, letterSpacing: "0.02em",
            mt: 1.2, mb: 0.3, pb: 0.4, borderBottom: `1px solid ${C.borderLight}`,
        }}>
            {children}
        </Typography>
    );
}

/** 입력칸과 같은 높이의 읽기 전용 값 — 수정 불가 항목을 빈칸처럼 보이지 않게 한다. */
function ReadOnlyValue({ value, hint }) {
    return (
        <Stack direction="row" alignItems="center" gap={0.75} sx={{
            height: 40, px: 1.2, borderRadius: 1,
            border: `1px solid ${C.border}`, bgcolor: C.bg,
        }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.textPrimary,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {value}
            </Typography>
            {hint && (
                <Typography sx={{ fontSize: 10.5, color: C.textMuted, ml: "auto", flexShrink: 0 }}>
                    {hint}
                </Typography>
            )}
        </Stack>
    );
}

/**
 * 차계부 — 법인차량 운행일지.
 *
 * <p>상단에 차량 카드(사진·차량번호·누적 KM)를 깔고, 카드를 고르면 아래에 그 차량의
 * 월별 운행일지가 종이 서식과 같은 표로 펼쳐진다. 기록은 누구나 쓸 수 있고,
 * 수정·삭제는 작성자 본인 또는 {@code VEHICLE_MANAGE} 권한자만 가능하다.
 *
 * <p>'주행 전 계기판' 은 그 차량의 직전 기록('주행 후')에서 자동으로 채워진다 —
 * 종이 일지에서 "마지막 탄 사람이 남긴 키로수" 를 다음 사람이 옮겨 적던 흐름을 그대로 옮긴 것.
 */
export default function VehicleLogPage() {
    const { hasPerm } = useMyPermissions();
    const canManage = hasPerm("VEHICLE_MANAGE");

    const [vehicles, setVehicles]   = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [anchor, setAnchor]       = useState(dayjs());     // 조회 중인 달
    const [monthly, setMonthly]     = useState(null);        // { rows, summary }
    const [purposes, setPurposes]   = useState([]);
    const [me, setMe]               = useState(null);
    const [loading, setLoading]     = useState(true);
    const [tableLoading, setTableLoading] = useState(false);
    const [saving, setSaving]       = useState(false);

    const [logForm, setLogForm]     = useState(null);        // null = 닫힘
    const [lastOdo, setLastOdo]     = useState(null);        // 직전 계기판 안내
    const [vehicleForm, setVehicleForm] = useState(null);    // 관리자 — 차량 등록/수정
    const [imageFile, setImageFile] = useState(null);        // 차량 폼에서 고른 새 사진
    const [imagePreview, setImagePreview] = useState(null);
    const fileInputRef = useRef(null);

    const selected = useMemo(
        () => vehicles.find(v => v.id === selectedId) || null,
        [vehicles, selectedId]);

    // ─── 차량 카드 가로 넘김 ──────────────────────────────────────
    // 차량이 한 화면(최대 5대)을 넘으면 줄을 늘리지 않고 좌우 화살표로 넘긴다.
    const stripRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft]   = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const syncArrows = useCallback(() => {
        const el = stripRef.current;
        if (!el) return;
        const slack = 4;   // 소수점 스크롤 오차 — 이만큼은 '끝에 닿았다'로 본다
        setCanScrollLeft(el.scrollLeft > slack);
        setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - slack);
    }, []);

    // 차량 목록이 바뀌거나 창 크기가 바뀌면 화살표 노출을 다시 계산
    useEffect(() => {
        syncArrows();
        window.addEventListener("resize", syncArrows);
        return () => window.removeEventListener("resize", syncArrows);
    }, [vehicles, syncArrows]);

    /** 한 화면 폭만큼 넘긴다. */
    const scrollStrip = (dir) => {
        const el = stripRef.current;
        if (el) el.scrollBy({ left: dir * el.clientWidth, behavior: "smooth" });
    };

    // 선택된 차량이 화면 밖이면 끌어온다 (삭제 후 첫 차량으로 넘어가는 경우 등)
    useEffect(() => {
        const el = stripRef.current;
        if (!el || selectedId == null) return;
        const card = el.querySelector(`[data-vehicle-id="${selectedId}"]`);
        card?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
    }, [selectedId]);

    // ─── 로딩 ────────────────────────────────────────────────────
    const loadVehicles = useCallback((keepSelection = true) => {
        setLoading(true);
        Promise.allSettled([
            vehicleApi.getVehicles(canManage),   // 관리자는 운행 종료 차량도 함께 본다
            vehicleApi.getPurposes(),
            vehicleApi.getMe(),
        ]).then(([v, p, m]) => {
            const list = v.status === "fulfilled" ? (v.value || []) : [];
            setVehicles(list);
            if (p.status === "fulfilled") setPurposes(p.value || []);
            if (m.status === "fulfilled") setMe(m.value || null);
            setSelectedId(prev => {
                if (keepSelection && prev && list.some(x => x.id === prev)) return prev;
                return list.length ? list[0].id : null;
            });
        }).finally(() => setLoading(false));
    }, [canManage]);

    useEffect(() => { loadVehicles(); }, [loadVehicles]);

    const loadMonthly = useCallback(() => {
        if (!selectedId) { setMonthly(null); return; }
        setTableLoading(true);
        vehicleApi.getMonthlyLogs(selectedId, anchor.year(), anchor.month() + 1)
            .then(d => setMonthly(d || null))
            .catch(() => setMonthly(null))
            .finally(() => setTableLoading(false));
    }, [selectedId, anchor]);

    useEffect(() => { loadMonthly(); }, [loadMonthly]);

    /** 카드의 누적 KM 만 갱신 (표는 건드리지 않는다). */
    const reloadVehicleCards = useCallback(() => {
        vehicleApi.getVehicles(canManage).then(d => setVehicles(d || [])).catch(() => {});
    }, [canManage]);

    /** 기록 변경 후 — 표와 카드(누적 KM)를 함께 갱신한다. */
    const reloadAll = useCallback(() => {
        loadMonthly();
        reloadVehicleCards();
    }, [loadMonthly, reloadVehicleCards]);

    // ─── 운행 기록 작성/수정 ──────────────────────────────────────
    const openLogNew = async () => {
        if (!selectedId) return;
        const form = emptyLog(selectedId);
        setLogForm(form);
        setLastOdo(null);
        try {
            const last = await vehicleApi.getLastLog(selectedId);
            setLastOdo(last || null);
            if (last?.odometer != null) {
                setLogForm(f => (f && !f.id ? { ...f, odometerBefore: formatNumberWithCommas(last.odometer) } : f));
            }
        } catch { /* 자동 입력 실패는 치명적이지 않다 — 직접 입력하면 된다 */ }
    };

    const openLogEdit = (row) => {
        setLastOdo(null);
        setLogForm({
            id: row.id,
            vehicleId: row.vehicleId,
            // 뒤에 다른 기록이 있으면 계기판·일자·목적·구간은 잠기고 주유·특이사항만 고칠 수 있다
            locked: Boolean(row.laterLogExists),
            canEdit: Boolean(row.canEdit),
            driveDate: row.driveDate,
            driverName: row.driverName || "",   // 표시 전용 (수정 불가)
            purpose: row.purpose || "BUSINESS",
            purposeDetail: row.purposeDetail || "",
            origin: row.origin || "",
            destination: row.destination || "",
            odometerBefore: formatNumberWithCommas(row.odometerBefore),
            odometerAfter: formatNumberWithCommas(row.odometerAfter),
            refuelAmount: formatNumberWithCommas(row.refuelAmount),
            note: row.note || "",
        });
    };

    const logDistance = useMemo(() => {
        if (!logForm) return null;
        const b = toInt(logForm.odometerBefore), a = toInt(logForm.odometerAfter);
        if (b == null || a == null) return null;
        return a - b;
    }, [logForm]);

    // 주유(여부·금액)를 뺀 나머지는 모두 필수 — 빈칸이 많은 일지는 나중에 아무 것도 확인할 수 없다.
    // 잠긴 기록(뒤에 다른 기록이 있음)은 주유·특이사항만 고칠 수 있으므로 그 두 가지만 본다.
    const logValid = Boolean(logForm) && (
        logForm.locked
            ? !!logForm.note?.trim()
            : (!!logForm.driveDate
                && !!logForm.purpose
                && !!logForm.purposeDetail?.trim()
                && !!logForm.origin?.trim()
                && !!logForm.destination?.trim()
                && !!logForm.note?.trim()
                && toInt(logForm.odometerBefore) != null
                && toInt(logForm.odometerAfter) != null
                && logDistance != null && logDistance >= 0)
    );

    const saveLog = async () => {
        const f = logForm;
        const payload = {
            // 잠긴 기록은 주유·특이사항만 보낸다 (서버도 나머지는 무시한다)
            ...(f.locked ? {} : {
                vehicleId: f.vehicleId,
                driveDate: f.driveDate,
                purpose: f.purpose,
                purposeDetail: f.purposeDetail || null,
                origin: f.origin || null,
                destination: f.destination || null,
                odometerBefore: toInt(f.odometerBefore),
                odometerAfter: toInt(f.odometerAfter),
            }),
            // 주유 여부는 서버가 금액에서 파생한다 (금액이 있으면 주유한 것)
            refuelAmount: toInt(f.refuelAmount),
            note: f.note || null,
        };
        const res = await runApiAction({
            request: () => f.id ? vehicleApi.updateLog(f.id, payload) : vehicleApi.createLog(payload),
            successMsg: f.id ? "운행 기록이 수정되었습니다." : "운행 기록이 등록되었습니다.",
            setLoading: setSaving, throwOnError: true,
        });
        if (res.ok) {
            setLogForm(null);
            // 다른 달에 쓴 기록이면 그 달로 옮겨 결과가 눈에 보이게 한다.
            // 이때 표는 anchor 변경에 걸린 effect 가 다시 불러오므로 여기서 또 부르지 않는다
            // (같은 화면에 두 개의 월 요청이 경합해 엉뚱한 달이 남는 걸 막는다).
            const d = dayjs(f.driveDate);
            if (d.isValid() && !d.isSame(anchor, "month")) setAnchor(d);
            else loadMonthly();
            reloadVehicleCards();   // 누적 KM 은 어느 경우든 갱신
        }
    };

    const deleteLog = async (row) => {
        const label = `${formatDate(row.driveDate)} ${row.driverName || ""}`.trim();
        if (!(await confirmDialog({
            message: `"${label}" 운행 기록을 삭제하시겠습니까?\n삭제하면 이 구간의 주행거리·주유 내역이 사라지고 되돌릴 수 없습니다.`,
        }))) return;
        await runApiAction({ request: () => vehicleApi.deleteLog(row.id), successMsg: "삭제되었습니다." });
        setLogForm(null);
        reloadAll();
    };

    // ─── 차량 등록/수정 (관리자) ──────────────────────────────────

    /** 미리보기용 object URL 정리 — 남겨두면 페이지를 떠날 때까지 메모리에 붙어 있는다. */
    const clearPreview = useCallback(() => {
        setImagePreview(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
        setImageFile(null);
    }, []);

    const openVehicleNew = () => {
        setVehicleForm(emptyVehicle());
        clearPreview();
    };
    const openVehicleEdit = (v) => {
        setVehicleForm({
            id: v.id, name: v.name || "", plateNo: v.plateNo || "", model: v.model || "",
            initialOdometer: formatNumberWithCommas(v.initialOdometer), memo: v.memo || "", active: v.active !== false,
            imageUrl: v.imageUrl || null,
            // 운행 기록이 하나도 없으면 등록 시 계기판을 아직 고칠 수 있다 (아래 odometerLocked)
            logCount: v.logCount || 0,
        });
        clearPreview();
    };

    const pickImage = (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";                       // 같은 파일 재선택도 동작하게
        if (!file) return;
        setImagePreview(prev => {
            if (prev) URL.revokeObjectURL(prev);
            return URL.createObjectURL(file);
        });
        setImageFile(file);
    };

    const closeVehicleForm = () => { setVehicleForm(null); clearPreview(); };

    const saveVehicle = async () => {
        const f = vehicleForm;
        const payload = {
            name: f.name, plateNo: f.plateNo, model: f.model || null,
            // 등록 시 계기판은 잠기지 않았을 때만 보낸다 (서버도 기록이 있으면 변경을 거부한다)
            ...(isOdometerLocked(f) ? {} : { initialOdometer: toInt(f.initialOdometer) }),
            memo: f.memo || null,
            active: f.active,
        };
        const res = await runApiAction({
            request: async () => {
                const saved = f.id ? await vehicleApi.updateVehicle(f.id, payload)
                                   : await vehicleApi.createVehicle(payload);
                // 차량은 이미 만들어졌으니 폼에 id 를 심는다 — 뒤이은 사진 업로드가 실패해
                // 사용자가 다시 저장을 눌러도 같은 차량을 또 만들지 않고 수정으로 이어진다.
                if (!f.id && saved?.id) setVehicleForm(v => (v ? { ...v, id: saved.id } : v));
                if (imageFile && saved?.id) await vehicleApi.uploadVehicleImage(saved.id, imageFile);
                return saved;
            },
            successMsg: f.id ? "차량 정보가 수정되었습니다." : "차량이 등록되었습니다.",
            setLoading: setSaving, throwOnError: true,
        });
        if (res.ok) {
            closeVehicleForm();
            loadVehicles();
        }
    };

    const removeVehicleImage = async () => {
        const f = vehicleForm;
        if (!f?.id) { clearPreview(); return; }
        if (!(await confirmDialog({ message: "차량 사진을 삭제하시겠습니까?" }))) return;
        await runApiAction({ request: () => vehicleApi.deleteVehicleImage(f.id), successMsg: "사진이 삭제되었습니다." });
        setVehicleForm(v => ({ ...v, imageUrl: null }));
        clearPreview();
        loadVehicles();
    };

    const deleteVehicle = async (v) => {
        const hasLogs = (v.logCount || 0) > 0;
        const message = hasLogs
            ? `"${v.name}" 차량에 운행 기록 ${v.logCount}건이 있습니다.\n기록 보존을 위해 삭제 대신 '운행 종료'로 처리됩니다. 계속할까요?`
            : `"${v.name}" 차량을 삭제하시겠습니까?`;
        if (!(await confirmDialog({ message }))) return;
        await runApiAction({
            request: () => vehicleApi.deleteVehicle(v.id),
            successMsg: hasLogs ? "운행 종료 처리되었습니다." : "삭제되었습니다.",
        });
        setVehicleForm(null);
        loadVehicles(false);
    };

    // ─── 파생값 ──────────────────────────────────────────────────
    const purposeOptions = purposes.map(p => ({ value: p.code, label: p.label }));
    // 뒤에 다른 기록이 있는 건 → 주유·특이사항만 수정 가능. 권한이 없으면 아예 읽기 전용.
    const logLocked   = Boolean(logForm?.locked);
    const logReadOnly = Boolean(logForm?.id && !logForm.canEdit);
    const rows = monthly?.rows || [];
    const summary = monthly?.summary || null;
    const isThisMonth = anchor.isSame(dayjs(), "month");

    const formPreviewUrl = imagePreview
        || (vehicleForm?.imageUrl ? safeImgUrl(vehicleForm.imageUrl) : null);

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1500, mx: "auto" }}>
            {/* 헤더 */}
            <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap" mb={2}>
                <Typography sx={ds.typography.pageTitle}>차계부</Typography>
                <Box sx={{ flex: 1 }} />
                {canManage && (
                    <ActionButton variant="secondary" startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                        onClick={openVehicleNew} label="차량 등록" sx={{ fontSize: 12 }} />
                )}
            </Stack>

            {loading ? <LoadingDots /> : (
                <>
                    {/* ─── 차량 카드 ─────────────────────────────── */}
                    {vehicles.length === 0 ? (
                        <Paper sx={{ ...ds.cardPadded, p: 4, textAlign: "center" }}>
                            <Typography sx={{ fontSize: 13, color: C.textMuted }}>
                                등록된 법인차량이 없습니다.{canManage ? " 우측 상단 '차량 등록'으로 추가하세요." : ""}
                            </Typography>
                        </Paper>
                    ) : (
                        // 카드는 한 줄만 쓴다. 화면 폭에 들어가는 만큼(2~5대)만 보이고,
                        // 그보다 많으면 줄을 늘리는 대신 좌우 화살표로 넘긴다.
                        <Box sx={{ position: "relative", mb: 2.5 }}>
                            <StripArrow side="left"  show={canScrollLeft}  onClick={() => scrollStrip(-1)} />
                            <StripArrow side="right" show={canScrollRight} onClick={() => scrollStrip(1)} />
                            <Box
                                ref={stripRef}
                                onScroll={syncArrows}
                                sx={{
                                    display: "flex", gap: 1.5,
                                    overflowX: "auto", overflowY: "hidden",
                                    scrollSnapType: "x mandatory",
                                    // 스크롤바는 숨기고 화살표로만 넘기게 한다 (트랙패드 스와이프는 그대로 동작)
                                    scrollbarWidth: "none",
                                    "&::-webkit-scrollbar": { display: "none" },
                                }}
                            >
                                {vehicles.map(v => (
                                    <Box
                                        key={v.id}
                                        data-vehicle-id={v.id}
                                        sx={{
                                            flex: "0 0 auto", scrollSnapAlign: "start",
                                            // gap(12px)을 뺀 폭을 n등분 — 카드가 잘려 보이지 않고 딱 맞게 채워진다
                                            width: {
                                                xs: "calc((100% - 12px) / 2)",
                                                sm: "calc((100% - 24px) / 3)",
                                                md: "calc((100% - 36px) / 4)",
                                                lg: "calc((100% - 48px) / 5)",
                                            },
                                        }}
                                    >
                                        <VehicleCard
                                            vehicle={v}
                                            selected={v.id === selectedId}
                                            canManage={canManage}
                                            onSelect={() => setSelectedId(v.id)}
                                            onEdit={() => openVehicleEdit(v)}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {/* ─── 월별 운행일지 ──────────────────────────── */}
                    {selected && (
                        <Paper sx={{ ...ds.cardPadded, p: 2, overflowX: "auto" }}>
                            {/* 툴바 — 월 이동 + 월 요약 */}
                            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" mb={1.5}>
                                <Stack direction="row" alignItems="center" gap={0.25}>
                                    <IconButton size="small" onClick={() => setAnchor(a => a.subtract(1, "month"))}>
                                        <ChevronLeftRoundedIcon sx={{ fontSize: 20 }} />
                                    </IconButton>
                                    <Typography sx={{ fontSize: 14.5, fontWeight: 800, minWidth: 108, textAlign: "center" }}>
                                        {anchor.format("YYYY년 M월")}
                                    </Typography>
                                    <IconButton size="small" onClick={() => setAnchor(a => a.add(1, "month"))}>
                                        <ChevronRightRoundedIcon sx={{ fontSize: 20 }} />
                                    </IconButton>
                                </Stack>
                                {!isThisMonth && (
                                    <ActionButton variant="secondary" label="이번 달" startIcon={false}
                                        onClick={() => setAnchor(dayjs())}
                                        sx={{ fontSize: 11.5, py: 0.2, minWidth: 0 }} />
                                )}
                                <Chip size="small" label={`${selected.name} · ${selected.plateNo}`}
                                    sx={{ height: 22, fontSize: 11.5, fontWeight: 700,
                                        bgcolor: C.primaryBg, color: C.primaryText }} />
                                <Box sx={{ flex: 1 }} />
                                {summary && <MonthSummary summary={summary} />}
                                {/* 기록 작성은 '지금 보고 있는 차량·달'에 쓰는 행위라 표 바로 위에 둔다 */}
                                <ActionButton variant="add" startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                                    onClick={openLogNew} label="운행 기록 작성" sx={{ ml: 0.5 }} />
                            </Stack>

                            {tableLoading ? <LoadingDots /> : (
                                <ResizableTable size="small" sx={ds.tableSx}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>순번</TableCell>
                                            <TableCell>일자</TableCell>
                                            <TableCell>운행자</TableCell>
                                            <TableCell>운행 목적</TableCell>
                                            <TableCell>출발지</TableCell>
                                            <TableCell>목적지</TableCell>
                                            <TableCell>주행 전 (km)</TableCell>
                                            <TableCell>주행 후 (km)</TableCell>
                                            <TableCell>주행거리 (km)</TableCell>
                                            <TableCell>주유</TableCell>
                                            <TableCell>특이사항</TableCell>
                                            <TableCell>작성자</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rows.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={12} sx={{ py: 5, color: C.textMuted, fontSize: 13 }}>
                                                    {anchor.format("YYYY년 M월")}에 등록된 운행 기록이 없습니다.
                                                </TableCell>
                                            </TableRow>
                                        ) : rows.map((r, i) => (
                                            // 수정·삭제는 행을 열어 모달 안에서만 — 표에 삭제 버튼을 두면 실수로 지우기 쉽다
                                            <LogRow key={r.id} row={r} index={i + 1} onOpen={() => openLogEdit(r)} />
                                        ))}
                                    </TableBody>
                                </ResizableTable>
                            )}
                            {rows.length > 0 && (
                                <Typography sx={{ fontSize: 11, color: C.textMuted, mt: 1 }}>
                                    행을 클릭하면 상세를 열어 수정·삭제할 수 있습니다.
                                </Typography>
                            )}
                        </Paper>
                    )}
                </>
            )}

            {/* ─── 운행 기록 작성/수정 모달 ───────────────────────── */}
            <FormModal
                open={Boolean(logForm)}
                onClose={() => setLogForm(null)}
                title={!logForm?.id ? "운행 기록 작성" : logReadOnly ? "운행 기록" : "운행 기록 수정"}
                subtitle={selected ? `${selected.name} · ${selected.plateNo}` : ""}
                maxWidth="sm"
                onSave={logReadOnly ? undefined : saveLog}
                saving={saving}
                saveDisabled={!logValid}
                cancelLabel={logReadOnly ? "닫기" : "취소"}
                footerExtras={logForm?.id && !logReadOnly ? (
                    <ActionButton variant="delete"
                        onClick={() => deleteLog({ id: logForm.id, driveDate: logForm.driveDate, driverName: logForm.driverName })} />
                ) : null}
            >
                {logForm && (
                    // 모든 줄을 같은 2열 그리드에 태운다 — 칸마다 폭을 따로 주면
                    // 왼쪽/오른쪽 칸의 시작·끝선이 어긋나 폼이 들쭉날쭉해 보인다.
                    <Stack gap={0.5}>
                        {/* 왜 잠겼는지 먼저 알려준다 — 칸이 회색인 이유를 폼 안에서 찾게 만들면 안 된다 */}
                        {(logLocked || logReadOnly) && (
                            <Stack direction="row" gap={0.75} alignItems="flex-start" sx={{
                                px: 1.2, py: 0.9, borderRadius: 1, mb: 0.5,
                                bgcolor: C.warningBg, border: `1px solid ${C.warning}`,
                            }}>
                                <WarningAmberRoundedIcon sx={{ fontSize: 15, color: C.warning, mt: 0.1 }} />
                                <Typography sx={{ fontSize: 11.5, color: C.warning, lineHeight: 1.5 }}>
                                    {logReadOnly
                                        ? "본인이 작성한 기록이 아니어서 열람만 할 수 있습니다."
                                        : "이 기록 뒤에 다른 운행 기록이 있습니다. 뒷 기록이 이 기록의 '주행 후' 계기판을 이어받았기 때문에 계기판·일자·목적·구간은 고칠 수 없고, 주유와 특이사항만 수정할 수 있습니다."}
                                </Typography>
                            </Stack>
                        )}

                        <SectionLabel>운행</SectionLabel>
                        <FormRow>
                            {/* 운행자는 로그인 사용자로 고정 — 서버도 클라이언트 값을 받지 않는다 */}
                            <FormField label="운행자" sx={FIELD_SX}>
                                <ReadOnlyValue
                                    value={logForm.id ? (logForm.driverName || "—") : (me?.name || "—")}
                                    hint="본인으로 고정"
                                />
                            </FormField>
                            <FormField label="운행 일자" required sx={FIELD_SX}>
                                <TextField fullWidth size="small" type="date" value={logForm.driveDate}
                                    disabled={logLocked || logReadOnly}
                                    inputProps={{ max: todayStr() }}
                                    onChange={e => setLogForm(f => ({ ...f, driveDate: e.target.value }))} />
                            </FormField>
                        </FormRow>

                        <SectionLabel>목적</SectionLabel>
                        <FormRow>
                            <FormField label="운행 목적" required sx={FIELD_SX}>
                                <ComboBox options={purposeOptions} value={logForm.purpose} clearable={false}
                                    disabled={logLocked || logReadOnly}
                                    onChange={(v) => setLogForm(f => ({ ...f, purpose: v || "BUSINESS" }))} />
                            </FormField>
                            <FormField label="목적 세부" required sx={FIELD_SX}>
                                <TextField fullWidth size="small" value={logForm.purposeDetail}
                                    disabled={logLocked || logReadOnly}
                                    placeholder="예: 온앤미 미팅, 행사 물품 운반"
                                    onChange={e => setLogForm(f => ({ ...f, purposeDetail: e.target.value }))} />
                            </FormField>
                        </FormRow>

                        <SectionLabel>위치</SectionLabel>
                        <FormRow>
                            <FormField label="출발지" required sx={FIELD_SX}>
                                <TextField fullWidth size="small" value={logForm.origin} placeholder="예: 사무실"
                                    disabled={logLocked || logReadOnly}
                                    onChange={e => setLogForm(f => ({ ...f, origin: e.target.value }))} />
                            </FormField>
                            <FormField label="목적지" required sx={FIELD_SX}>
                                <TextField fullWidth size="small" value={logForm.destination} placeholder="예: 서울시청"
                                    disabled={logLocked || logReadOnly}
                                    onChange={e => setLogForm(f => ({ ...f, destination: e.target.value }))} />
                            </FormField>
                        </FormRow>

                        {/* 계기판 — 주행 전은 직전 기록에서 자동으로 채워진다 */}
                        <SectionLabel>계기판</SectionLabel>
                        <FormRow>
                            <FormField label="주행 전 계기판" required sx={FIELD_SX}>
                                <TextField fullWidth size="small" value={logForm.odometerBefore}
                                    disabled={logLocked || logReadOnly}
                                    inputProps={{ inputMode: "numeric" }}
                                    InputProps={{ endAdornment: <InputAdornment position="end">km</InputAdornment> }}
                                    onChange={e => setLogForm(f => ({ ...f, odometerBefore: commaInput(e.target.value) }))} />
                            </FormField>
                            <FormField label="주행 후 계기판" required sx={FIELD_SX}>
                                <TextField fullWidth size="small" value={logForm.odometerAfter}
                                    disabled={logLocked || logReadOnly}
                                    inputProps={{ inputMode: "numeric" }}
                                    InputProps={{ endAdornment: <InputAdornment position="end">km</InputAdornment> }}
                                    onChange={e => setLogForm(f => ({ ...f, odometerAfter: commaInput(e.target.value) }))} />
                            </FormField>
                        </FormRow>

                        {/* 주행거리는 입력이 아니라 계산 결과 — 입력칸 줄에서 빼내 한 줄로 보여준다 */}
                        <Stack direction="row" alignItems="center" gap={1} sx={{
                            px: 1.2, py: 0.8, mb: 0.5, borderRadius: 1,
                            bgcolor: logDistance != null && logDistance < 0 ? C.errorBg : C.bg,
                            border: `1px solid ${logDistance != null && logDistance < 0 ? C.error : C.border}`,
                        }}>
                            <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: C.textMuted }}>주행거리</Typography>
                            <Typography sx={{
                                fontSize: 14, fontWeight: 800, fontVariantNumeric: "tabular-nums",
                                color: logDistance == null ? C.textMuted : logDistance < 0 ? C.error : C.textPrimary,
                            }}>
                                {logDistance == null ? "—" : `${formatNumberWithCommas(logDistance)} km`}
                            </Typography>
                            <Box sx={{ flex: 1 }} />
                            <Typography sx={{ fontSize: 11, color: logDistance != null && logDistance < 0 ? C.error : C.textMuted,
                                textAlign: "right" }}>
                                {logDistance != null && logDistance < 0
                                    ? "주행 후 계기판이 주행 전보다 작습니다."
                                    : !logForm.id && lastOdo
                                        ? (lastOdo.fromLog
                                            ? `직전 기록(${formatDate(lastOdo.lastDriveDate)} · ${lastOdo.lastDriverName || "―"}) 주행 후 ${kmText(lastOdo.odometer)}km 를 불러왔습니다`
                                            : lastOdo.odometer != null
                                                ? `기록이 없어 차량 등록 시 계기판(${kmText(lastOdo.odometer)}km)을 불러왔습니다`
                                                : "이 차량의 첫 기록입니다")
                                        : ""}
                            </Typography>
                        </Stack>

                        <SectionLabel>주유</SectionLabel>
                        <FormRow>
                            {/* 별도의 '주유 여부' 체크는 두지 않는다 — 금액을 적었으면 주유한 것.
                                체크와 금액이 어긋난 기록이 생길 여지를 없앤다. */}
                            <FormField label="주유 금액" sx={FIELD_SX}>
                                <TextField fullWidth size="small" value={logForm.refuelAmount}
                                    disabled={logReadOnly}
                                    inputProps={{ inputMode: "numeric" }}
                                    placeholder="주유했다면 금액 입력"
                                    helperText="비워두면 주유 안 함으로 기록됩니다"
                                    FormHelperTextProps={{ sx: { fontSize: 10.5, mx: 0, mt: 0.4, color: C.textMuted } }}
                                    InputProps={{ endAdornment: <InputAdornment position="end">원</InputAdornment> }}
                                    onChange={e => setLogForm(f => ({ ...f, refuelAmount: commaInput(e.target.value) }))} />
                            </FormField>
                        </FormRow>

                        <SectionLabel>기타</SectionLabel>

                        <FormField label="특이사항" required>
                            <TextField fullWidth size="small" multiline minRows={2} value={logForm.note}
                                disabled={logReadOnly}
                                placeholder="차량 이상(소음·경고등), 접촉, 세차 등 다음 운행자가 알아야 할 내용. 없으면 '이상 없음'"
                                onChange={e => setLogForm(f => ({ ...f, note: e.target.value }))} />
                        </FormField>
                    </Stack>
                )}
            </FormModal>

            {/* ─── 차량 등록/수정 모달 (관리자) ───────────────────── */}
            <FormModal
                open={Boolean(vehicleForm)}
                onClose={closeVehicleForm}
                title={vehicleForm?.id ? "차량 정보 수정" : "차량 등록"}
                maxWidth="sm"
                onSave={saveVehicle}
                saving={saving}
                saveDisabled={!vehicleForm?.name?.trim() || !vehicleForm?.plateNo?.trim()}
                footerExtras={vehicleForm?.id ? (
                    <ActionButton variant="delete" onClick={() => deleteVehicle(
                        vehicles.find(v => v.id === vehicleForm.id) || { id: vehicleForm.id, name: vehicleForm.name })} />
                ) : null}
            >
                {vehicleForm && (
                    <Stack gap={0.5}>
                        <SectionLabel>사진</SectionLabel>
                        <FormField label="차량 사진">
                            <Stack direction="row" gap={1.5} alignItems="flex-start">
                                <Box sx={{
                                    width: 160, height: 108, flexShrink: 0, borderRadius: 1.5,
                                    border: `1px solid ${C.border}`, bgcolor: C.bg,
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    overflow: "hidden",
                                }}>
                                    {formPreviewUrl
                                        ? <Box component="img" src={formPreviewUrl} alt="차량 사진"
                                            sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                        : <DirectionsCarRoundedIcon sx={{ fontSize: 40, color: C.textMuted }} />}
                                </Box>
                                <Stack gap={0.75}>
                                    <ActionButton variant="secondary" label="사진 선택"
                                        startIcon={<PhotoCameraRoundedIcon sx={{ fontSize: 15 }} />}
                                        onClick={() => fileInputRef.current?.click()}
                                        sx={{ fontSize: 12 }} />
                                    {(formPreviewUrl) && (
                                        <ActionButton variant="secondary" label="사진 제거"
                                            startIcon={<ImageNotSupportedRoundedIcon sx={{ fontSize: 15 }} />}
                                            onClick={removeVehicleImage} sx={{ fontSize: 12 }} />
                                    )}
                                    <Typography sx={{ fontSize: 11, color: C.textMuted }}>
                                        JPG·PNG 이미지. 카드에 가로로 채워 표시됩니다.
                                    </Typography>
                                </Stack>
                            </Stack>
                            <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={pickImage} />
                        </FormField>

                        <SectionLabel>차량</SectionLabel>
                        <FormRow>
                            <FormField label="차량 이름" required sx={FIELD_SX}>
                                <TextField fullWidth size="small" value={vehicleForm.name} placeholder="예: 보타렉스"
                                    onChange={e => setVehicleForm(v => ({ ...v, name: e.target.value }))} />
                            </FormField>
                            <FormField label="차량번호" required sx={FIELD_SX}>
                                <TextField fullWidth size="small" value={vehicleForm.plateNo} placeholder="예: 81보 8883"
                                    onChange={e => setVehicleForm(v => ({ ...v, plateNo: e.target.value }))} />
                            </FormField>
                        </FormRow>

                        <FormRow>
                            <FormField label="차종" sx={FIELD_SX}>
                                <TextField fullWidth size="small" value={vehicleForm.model} placeholder="예: 스타렉스"
                                    onChange={e => setVehicleForm(v => ({ ...v, model: e.target.value }))} />
                            </FormField>
                            {/* 등록 시 계기판은 첫 운행일지의 기준점이라, 기록이 쌓인 뒤 바꾸면 누적 KM 이 꼬인다.
                                아직 기록이 없는 차량은 꼬일 대상이 없으니 그대로 열어둔다. */}
                            <FormField label="등록 시 계기판" sx={FIELD_SX}>
                                <TextField fullWidth size="small" value={vehicleForm.initialOdometer}
                                    disabled={isOdometerLocked(vehicleForm)}
                                    inputProps={{ inputMode: "numeric" }}
                                    placeholder="첫 기록의 '주행 전' 기본값"
                                    helperText={odometerHelper(vehicleForm)}
                                    FormHelperTextProps={{ sx: { fontSize: 10.5, mx: 0, mt: 0.4, color: C.textMuted } }}
                                    InputProps={{ endAdornment: <InputAdornment position="end">km</InputAdornment> }}
                                    onChange={e => setVehicleForm(v => ({ ...v, initialOdometer: commaInput(e.target.value) }))} />
                            </FormField>
                        </FormRow>

                        <SectionLabel>기타</SectionLabel>
                        <FormField label="메모">
                            <TextField fullWidth size="small" multiline minRows={2} value={vehicleForm.memo}
                                placeholder="보험사, 정기권 연장일 등 참고 사항"
                                onChange={e => setVehicleForm(v => ({ ...v, memo: e.target.value }))} />
                        </FormField>
                    </Stack>
                )}
            </FormModal>
        </Box>
    );
}

/* ─── 차량 카드 ──────────────────────────────────────────────── */

function VehicleCard({ vehicle: v, selected, canManage, onSelect, onEdit }) {
    const imgUrl = v.imageUrl ? safeImgUrl(v.imageUrl) : null;
    const retired = v.active === false;
    return (
        <Paper
            onClick={onSelect}
            sx={{
                ...ds.card, p: 1.25, cursor: "pointer", position: "relative",
                border: `${selected ? 2 : 1}px solid ${selected ? C.primary : C.border}`,
                boxShadow: selected ? "0 2px 10px rgba(37,99,235,0.16)" : "none",
                // 선택 안 된 카드는 은은하게 죽인다 — 지금 보고 있는 차량이 한눈에 들어오도록.
                // 톤만 낮추고 대비는 남겨서 다른 차 정보도 계속 읽힌다.
                filter: selected ? "none" : "grayscale(0.75)",
                opacity: retired ? 0.45 : (selected ? 1 : 0.6),
                transition: "filter .16s, opacity .16s, border-color .12s, box-shadow .12s",
                "&:hover": {
                    borderColor: selected ? C.primary : C.primaryBorder,
                    filter: "none",
                    opacity: retired ? 0.7 : 1,
                },
            }}
        >
            {/* 사진 — 차종마다 이미지 비율이 제각각이라 잘라내지 않고(contain) 여백 안에 통째로 앉힌다.
                cover 로 채우면 탑차·경차처럼 비율이 다른 차가 앞뒤로 잘려 보였다.
                배경·테두리 없이 카드 흰 면 위에 그대로 — 차량만 떠 보이게 한다. */}
            <Box sx={{
                position: "relative", width: "100%", pt: CARD_IMAGE_RATIO,
                overflow: "hidden", mb: 1,
            }}>
                <Box sx={{
                    position: "absolute", inset: 0, p: 1,
                    display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                    {imgUrl
                        ? <Box component="img" src={imgUrl} alt={v.name}
                            sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                        : <DirectionsCarRoundedIcon sx={{ fontSize: 42, color: C.textMuted }} />}
                </Box>
            </Box>

            {/* 이름 + 관리 버튼 */}
            <Stack direction="row" alignItems="center" gap={0.5} mb={0.75}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: C.textPrimary, flex: 1, minWidth: 0,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {v.name}
                </Typography>
                {v.active === false && (
                    <Chip label="운행종료" size="small"
                        sx={{ height: 17, fontSize: 9.5, fontWeight: 700, bgcolor: C.borderLight, color: C.textMuted }} />
                )}
                {canManage && (
                    <Tooltip title="차량 정보 수정">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(); }}
                            sx={{ p: 0.25 }}>
                            <EditRoundedIcon sx={{ fontSize: 14, color: C.textMuted }} />
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>

            {/* 차량번호 · 누적 KM — 목업의 두 줄 정보 */}
            <Stack gap={0.5}>
                <CardPill label="차량 번호" value={v.plateNo} />
                <CardPill label="누적 KM" value={v.odometer != null ? `${formatNumberWithCommas(v.odometer)} km` : "미등록"} />
            </Stack>

        </Paper>
    );
}

/**
 * 카드 안의 라벨-값 블록. 라벨 위 / 값 아래의 수직 배치 —
 * 좌우로 벌려놓으면 카드 폭이 좁아질 때 값이 라벨에 붙어 읽기 어려워진다.
 * 배경·테두리 없이 글자만 — 카드 안에서 상자가 겹치면 시선이 분산된다.
 */
function CardPill({ label, value }) {
    return (
        <Box>
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: C.textMuted, lineHeight: 1.45 }}>
                {label}
            </Typography>
            <Typography sx={{
                fontSize: 13.5, fontWeight: 800, color: C.textPrimary, lineHeight: 1.35,
                fontVariantNumeric: "tabular-nums",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
                {value}
            </Typography>
        </Box>
    );
}

/**
 * 차량 카드 줄을 좌우로 넘기는 화살표 — 배경 없이 회색 홑화살표만.
 * 카드 줄 바깥(페이지 여백)에 놓아 카드를 가리지 않는다. 넘길 게 없으면 사라진다.
 */
function StripArrow({ side, show, onClick }) {
    if (!show) return null;
    const isLeft = side === "left";
    return (
        <IconButton
            disableRipple
            onClick={onClick}
            aria-label={isLeft ? "이전 차량" : "다음 차량"}
            sx={{
                position: "absolute", top: "50%", transform: "translateY(-50%)",
                [isLeft ? "left" : "right"]: -26,
                zIndex: 2, p: 0.25,
                color: C.textMuted,
                "&:hover": { bgcolor: "transparent", color: C.textPrimary },
            }}
        >
            {isLeft
                ? <ChevronLeftRoundedIcon sx={{ fontSize: 30 }} />
                : <ChevronRightRoundedIcon sx={{ fontSize: 30 }} />}
        </IconButton>
    );
}

/* ─── 월 요약 ────────────────────────────────────────────────── */

function MonthSummary({ summary: s }) {
    return (
        <Stack direction="row" gap={0.75} flexWrap="wrap" alignItems="center">
            <SummaryStat label="기록" value={`${s.count}건`} />
            <SummaryStat label="총 주행" value={`${formatNumberWithCommas(s.totalDistance)} km`} />
            <SummaryStat label="업무용" value={`${formatNumberWithCommas(s.businessDistance)} km`} />
            <SummaryStat label="주유" value={`${s.refuelCount}회 · ${formatKrw(s.totalRefuelAmount)}`} />
            {s.noteCount > 0 && (
                <SummaryStat label="특이사항" value={`${s.noteCount}건`} warn />
            )}
        </Stack>
    );
}

function SummaryStat({ label, value, warn = false }) {
    return (
        <Stack direction="row" alignItems="baseline" gap={0.5} sx={{
            px: 1, py: 0.35, borderRadius: 0.75,
            bgcolor: warn ? C.warningBg : C.bg,
            border: `1px solid ${warn ? C.warning : C.border}`,
        }}>
            <Typography sx={{ fontSize: 10.5, color: warn ? C.warning : C.textMuted, fontWeight: 700 }}>{label}</Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: warn ? C.warning : C.textPrimary,
                fontVariantNumeric: "tabular-nums" }}>
                {value}
            </Typography>
        </Stack>
    );
}

/* ─── 운행일지 행 ────────────────────────────────────────────── */

function LogRow({ row: r, index, onOpen }) {
    return (
        <TableRow hover onClick={onOpen} sx={{ cursor: "pointer" }}>
            <TableCell sx={{ color: C.textMuted }}>{index}</TableCell>
            <TableCell sx={{ whiteSpace: "nowrap" }}>{dayjs(r.driveDate).format("M/D (ddd)")}</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>{r.driverName || "—"}</TableCell>
            <TableCell>
                <Stack alignItems="center" gap={0.15}>
                    <Chip label={r.purposeLabel || "—"} size="small" sx={{
                        height: 18, fontSize: 10.5, fontWeight: 700,
                        bgcolor: r.business ? C.primaryBg : C.borderLight,
                        color: r.business ? C.primaryText : C.textSecondary,
                    }} />
                    {r.purposeDetail && (
                        <Typography sx={{ fontSize: 10.5, color: C.textMuted, lineHeight: 1.3 }}>
                            {r.purposeDetail}
                        </Typography>
                    )}
                </Stack>
            </TableCell>
            <TableCell>{r.origin || "—"}</TableCell>
            <TableCell>{r.destination || "—"}</TableCell>
            <TableCell align="right">
                <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={0.4}>
                    {r.odometerGap != null && r.odometerGap !== 0 && (
                        <Tooltip title={`직전 기록의 주행 후와 ${formatNumberWithCommas(Math.abs(r.odometerGap))}km 차이가 납니다 (미기록 운행이나 오기입일 수 있습니다)`}>
                            <WarningAmberRoundedIcon sx={{ fontSize: 14, color: C.warning }} />
                        </Tooltip>
                    )}
                    <span>{kmText(r.odometerBefore)}</span>
                </Stack>
            </TableCell>
            <TableCell align="right">{kmText(r.odometerAfter)}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 800 }}>{kmText(r.distanceKm)}</TableCell>
            <TableCell>
                {r.refueled ? (
                    <Stack direction="row" alignItems="center" justifyContent="center" gap={0.3}>
                        <LocalGasStationRoundedIcon sx={{ fontSize: 14, color: C.success }} />
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.success }}>
                            {r.refuelAmount != null ? formatKrw(r.refuelAmount) : "주유"}
                        </Typography>
                    </Stack>
                ) : <Typography sx={{ fontSize: 12, color: C.textMuted }}>—</Typography>}
            </TableCell>
            <TableCell align="left">
                {r.note ? (
                    <Tooltip title={r.note}>
                        <Typography sx={{
                            fontSize: 12, color: C.textSecondary, maxWidth: 220,
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}>
                            {r.note}
                        </Typography>
                    </Tooltip>
                ) : <Typography sx={{ fontSize: 12, color: C.textMuted }}>—</Typography>}
            </TableCell>
            <TableCell sx={{ color: C.textSecondary, fontSize: 12 }}>{r.createdByName || "—"}</TableCell>
        </TableRow>
    );
}
